import {AppbarProps, PanelAppbar as Appbar} from "../../layout";
import React, {FunctionComponent, useCallback, useMemo} from "react";
import {useRouteMatch} from "react-router-dom";
import useLogoutEndAction from "./logout-action";
import useSupportChatEndAction from "./support-chat-action";
import useBizLearnVideosEndAction from "./biz-learn-videos-action";
import usePluginsEndAction from "./plugins-action";
import useNotificationsEndAction from "./notifications-action";
import useLookupStartAction from "./lookup-action";
import useQuickOrderStartAction from "./quickorder-action";
import useDashboardStartAction from "./dashboard-action";
import useConnectedAppsEndAction from "./connected-apps-actions";
import PanelCommandSearch, {PanelCommandSearchScopeBridge} from "./panel-command-search";
import PanelAppbarNexusSearchBarComponent from "./nexus-searchbar-component";
import useSystemHealthEndAction from "./system-health";
import useSettingsEndAction from "./settings-action";
import useAccessLevels from "../../../hooks/use-access-levels";
import {routes} from "../../../../core/models/static/routes/app-routes";
import {type AppBarSearchMode, AppBarSearchModeToggler, useAppBarSearchMode,} from "./search-mode";


export type StartActionType = ArrayElementType<Required<AppbarProps>["startActions"]>;
export type EndActionType = ArrayElementType<Required<AppbarProps>["endActions"]>;

const startActionBreakpoint = 'sm';
const endActionBreakpoint = 'md';

const PanelAppbar: FunctionComponent = () => {
    const operatorAccessLevels = useAccessLevels();
    const lookupSearchMatch = useRouteMatch(routes.lookup.search);

    const {
        showToggle,
        effectiveMode,
        setMode,
    } = useAppBarSearchMode();

    const lookupStartAction = useLookupStartAction(startActionBreakpoint);
    const dashboardStartAction = useDashboardStartAction(startActionBreakpoint);
    const quickOrderStartAction = useQuickOrderStartAction(startActionBreakpoint);

    const logoutEndAction = useLogoutEndAction();
    const supportChatEndAction = useSupportChatEndAction();
    const bizLearnVideosEncAction = useBizLearnVideosEndAction(endActionBreakpoint);
    const notificationEndAction = useNotificationsEndAction(endActionBreakpoint);
    const pluginsEndAction = usePluginsEndAction(endActionBreakpoint);
    const connectedAppsEndAction = useConnectedAppsEndAction(endActionBreakpoint);
    const systemHealthEndAction = useSystemHealthEndAction(endActionBreakpoint);
    const settingsEndAction = useSettingsEndAction();

    const startActions = useMemo<Array<StartActionType>>(
        () => [
            ...(lookupStartAction ? [lookupStartAction] : []),
            ...(dashboardStartAction ? [dashboardStartAction] : []),
            ...(quickOrderStartAction ? [quickOrderStartAction] : []),
        ], [
            lookupStartAction,
            dashboardStartAction,
            quickOrderStartAction,
        ]);

    const noStartActions = useMemo(() => !startActions.length, [startActions]);

    const endActions = useMemo<Array<EndActionType>>(
        () => [
            ...(!pluginsEndAction ? [] : [pluginsEndAction as any]),
            ...(!bizLearnVideosEncAction ? [] : [bizLearnVideosEncAction as any]),
            supportChatEndAction,
            ...(!notificationEndAction ? [] : [notificationEndAction as any]),
            ...(!connectedAppsEndAction ? [] : [connectedAppsEndAction as any]),
            ...(systemHealthEndAction ? [systemHealthEndAction] : []),
            settingsEndAction,
            logoutEndAction,
        ], [
            pluginsEndAction,
            bizLearnVideosEncAction,
            supportChatEndAction,
            notificationEndAction,
            connectedAppsEndAction,
            systemHealthEndAction,
            settingsEndAction,
            logoutEndAction,
        ]);

    /**
     * Persists the operator's preferred search mode.
     */
    const handleSearchModeChange = useCallback((next: AppBarSearchMode) => {
        void setMode(next);
    }, [setMode]);

    const hasNexusAccess = operatorAccessLevels.packages.lookup.nexusSearch;

    const searchModeToggler = useMemo(() => {
        if (!showToggle || !hasNexusAccess) {
            return null;
        }
        return (
            <AppBarSearchModeToggler
                mode={effectiveMode}
                onChange={handleSearchModeChange}
            />
        );
    }, [effectiveMode, handleSearchModeChange, hasNexusAccess, showToggle]);

    const searchbarComponent = useMemo(() => {
        // Legacy mutually-exclusive path when the dual-mode rollout flag is off.
        if (!showToggle) {
            if (lookupSearchMatch || !hasNexusAccess) {
                return <span/>;
            }
            return <PanelAppbarNexusSearchBarComponent noStartActions={noStartActions}/>;
        }

        // Dual-mode: keep the operator's saved preference on every route (including Nexus page).
        if (effectiveMode === 'system' || !hasNexusAccess) {
            return (
                <PanelCommandSearch
                    noStartActions={noStartActions}
                    stretchToSearchSlot
                    triggerEndAdornment={searchModeToggler}
                />
            );
        }

        return (
            <PanelAppbarNexusSearchBarComponent
                noStartActions={noStartActions}
                searchModeToggler={searchModeToggler}
            />
        );
    }, [
        effectiveMode,
        hasNexusAccess,
        lookupSearchMatch,
        noStartActions,
        searchModeToggler,
        showToggle,
    ]);

    return (
        <>
            <Appbar
                startActionBreakpoint={startActionBreakpoint}
                endActionBreakpoint={endActionBreakpoint}
                startActions={startActions}
                endActions={endActions}
                searchbarComponent={searchbarComponent}
            />
            {showToggle && <PanelCommandSearchScopeBridge/>}
        </>
    );
};


export default PanelAppbar;
